"use client";

import type { iSupplier } from "@sdk/suppliers/supplier-model";
import type { ProductType } from "@sdk/bookings/booking-model";
import { PRODUCT_TYPE_LABEL } from "./add-edit-supplier-modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Archive, Pencil, Truck } from "lucide-react";
import { cn } from "@/lib/utils";

function formatProductTypesLabel(types: string[]): string {
  if (!types?.length) return "—";
  return types
    .map((t) =>
      t in PRODUCT_TYPE_LABEL ? PRODUCT_TYPE_LABEL[t as ProductType] : t
    )
    .join(", ");
}

export interface SupplierCardProps {
  supplier: iSupplier;
  onEdit: () => void;
  onArchive?: () => void;
}

export function SupplierCard({
  supplier,
  onEdit,
  onArchive,
}: SupplierCardProps) {
  const productTypesLabel = formatProductTypesLabel(
    supplier.productTypes ?? []
  );
  const isArchived = supplier.archived === true;

  return (
    <Card
      className={cn(
        "w-[300px] flex flex-col transition-colors hover:bg-muted/50",
        isArchived && "opacity-70"
      )}
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 p-4 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Truck className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="font-semibold truncate">{supplier.name}</span>
          {isArchived ? (
            <Badge variant="secondary" className="shrink-0 text-xs">
              Archived
            </Badge>
          ) : null}
        </div>
        <div
          className="flex items-center gap-0.5 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            aria-label="Edit supplier"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {!isArchived && onArchive ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onArchive();
              }}
              aria-label="Archive supplier"
            >
              <Archive className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <p className="text-sm text-muted-foreground truncate" title={productTypesLabel}>
          {productTypesLabel}
        </p>
      </CardContent>
    </Card>
  );
}
